import { useState, useEffect } from 'react';
import { t } from 'i18next';
import { useColorMode } from '@chakra-ui/react';
import { attachJosa, parseMemberCalledMessage } from '../../../utils/bettieUtils';
import ProfileAvatar from '../../my-page/components/ProfileAvatar';
import PulsingGradientCircle from './PulsingGradientCircle';
import SpeechBallon from './SpeechBallon';
import type { BettieSettingStep } from '../BettieSettingPage';
import type { components } from '../../../types/schema';

type ApiV1MemberPersona = components['schemas']['ApiV1MemberPersona'];

interface BettieLoadingProps {
  bettieName: string;
  bettiePersona: ApiV1MemberPersona;
  setCurrentStep: (step: BettieSettingStep) => void;
}

const BettieLoading = ({ bettieName, bettiePersona, setCurrentStep }: BettieLoadingProps) => {
  const [showChatLoading, setShowChatLoading] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowChatLoading(true);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  if (showChatLoading) {
    return (
      <ChatLoading
        bettieName={bettieName}
        bettiePersona={bettiePersona}
        setCurrentStep={setCurrentStep}
      />
    );
  }
  return <CircleLoadingPage bettieName={bettieName} />;
};

export default BettieLoading;

const CircleLoadingPage = ({ bettieName }: { bettieName: string }) => {
  const { colorMode } = useColorMode();
  const [dots, setDots] = useState('');

  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => {
        if (prev.length >= 3) {
          return '';
        }
        return prev + '.';
      });
    }, 500);

    return () => clearInterval(interval);
  }, []);

  return (
    <div
      className="min-h-screen fixed inset-0 flex flex-col items-center justify-center"
      style={{
        backgroundImage: `url(/assets/bettie/bg_bettie_loading1_${colorMode}.png)`,
        backgroundRepeat: 'round'
      }}
    >
      <PulsingGradientCircle
        gradientCircleSize="301px"
        glassCircleSize="296px"
        dottedCircleSize="237px"
        showDottedCircle={true}
        blur="11px"
      >
        <div className="flex flex-col items-center gap-[15px]">
          <p className="text-[26px] font-bold text-text-main-lv2 leading-[31px] whitespace-pre-line">
            {t('BettieSetting.loading.circleLoadingTitle', {
              nickname: attachJosa(bettieName, '을/를')
            })}
            <span className="inline-block w-[30px] text-left">{dots}</span>
          </p>
          <p className="text-[14px] font-medium text-text-main-lv2 leading-[17px]">
            {t('BettieSetting.loading.circleLoadingDescription')}
          </p>
        </div>
      </PulsingGradientCircle>
    </div>
  );
};

const ChatLoading = ({
  bettieName,
  bettiePersona,
  setCurrentStep
}: {
  bettieName: string;
  bettiePersona: ApiV1MemberPersona;
  setCurrentStep: (step: BettieSettingStep) => void;
}) => {
  const [showFirst, setShowFirst] = useState(true);
  const { colorMode } = useColorMode();
  return (
    <div
      className="min-h-screen pt-[calc(env(safe-area-inset-top)+55px)] pb-[env(safe-area-inset-bottom)] fixed inset-0 flex flex-col items-center justify-center"
      style={{
        backgroundImage: `url(/assets/bettie/bg_bettie_loading2_${colorMode}.png)`,
        backgroundRepeat: 'round'
      }}
    >
      <div className="absolute top-[calc(env(safe-area-inset-top)+55px)] left-0 flex flex-col items-start gap-[14px] px-[24px] py-[10px] z-[999]">
        <h1 className="text-[26px] font-bold text-text-main-lv1 leading-[31px] whitespace-pre-line">
          {t('BettieSetting.loading.chatLoadingTitle', {
            nickname: attachJosa(bettieName, '을/를')
          })}
        </h1>
        <p className="text-[15px] font-medium text-text-main-lv4 leading-[18px]">
          {t('BettieSetting.loading.chatLoadingDescription')}
        </p>
      </div>
      <div className="flex flex-col items-center gap-[8px] mb-[16px] z-10">
        <SpeechBallon
          text={
            showFirst
              ? parseMemberCalledMessage(bettiePersona.memberCalledMessage, {
                  member_name: bettieName
                })
              : parseMemberCalledMessage(bettiePersona.profileImageMessage, {
                  member_name: bettieName
                })
          }
          onTypingComplete={() => {
            if (showFirst) {
              // 첫 문구 타이핑 완료 후 1초 뒤에 다음 문구로
              setTimeout(() => setShowFirst(false), 1000);
            } else {
              // 두 번째 문구 타이핑 완료 후 1초 뒤에 다음 스텝으로
              setTimeout(() => {
                setCurrentStep('profile_image');
              }, 1000);
            }
          }}
        />
      </div>
      <div className="flex flex-col items-center gap-[16px]">
        <PulsingGradientCircle
          gradientCircleSize="153px"
          glassCircleSize="149px"
          showDottedCircle={false}
          blur="11px"
        >
          <ProfileAvatar widthAndHeight="138px" svgWidthAndHeight="97px" />
        </PulsingGradientCircle>
        <p className="text-[16px] font-semibold text-text-main-lv2 leading-[19px]">{bettieName}</p>
      </div>
    </div>
  );
};
